import { createColumnHelper } from '@tanstack/react-table';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import { CommandCenterPersonProfile } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { PageContent } from '@/components/ui/page-content';
import { formatPhoneNumber, getPersonOrgName } from 'utils';
import { formatDate } from 'utils/date-formatter';
import { calculateAge } from 'utils/date-utils';
import {
    CustomTablePage,
    DateTimeDisplay,
    HorizontalLayout,
    PersonLogoCard,
    Text,
    Tooltip,
    VerticalLayout,
} from 'widgets';
import { CopyToClipboard } from 'widgets/CopyToClipboard';
import { IconEnvelop, IconPhone } from 'widgets/icon';
import { TextLink } from 'widgets/Text';

import { usePersonProfilesForTenants } from './person-queries';
import { PersonDetailsDrawer } from './PersonDetailsDrawer';
import { HouseholdFilterForm } from './PersonFilters/PersonFiltersForm';
import { usePersonFilters } from './PersonFilters/PersonFiltersHook';

const columnHelper = createColumnHelper<CommandCenterPersonProfile>();

// Content component without wrapper - can be reused with custom header
export const PersonListContent = ({
    centerContent,
    exportPortalId,
    rightActions,
}: {
    centerContent?: React.ReactNode;
    exportPortalId?: string;
    rightActions?: React.ReactNode;
}) => {
    const { data: personProfiles, isLoading } = usePersonProfilesForTenants();
    const { data: filteredPersons } = usePersonFilters(personProfiles ?? []);
    const [selectedPerson, setSelectedPerson] = useState<CommandCenterPersonProfile | null>();

    return (
        <>
            <HouseholdFilterForm
                centerContent={centerContent}
                count={isLoading ? undefined : filteredPersons?.length}
                rightActions={rightActions}
            />

            {selectedPerson ? (
                <PersonDetailsDrawer person={selectedPerson} onClose={() => setSelectedPerson(null)} />
            ) : null}
            <PageContent scroll="children" padding="none">
                <CustomTablePage
                    rowOnClick={(person) => setSelectedPerson(person)}
                    columns={columns}
                    data={filteredPersons}
                    defaultSortCol="lastLogin"
                    isLoading={isLoading}
                    exportName="persons"
                    exportPortalId={exportPortalId}
                    cardClassName="m-0 border-0 shadow-none"
                />
            </PageContent>
        </>
    );
};

const columns = [
    columnHelper.accessor((data) => getPersonOrgName(data.personOrgShortProfile), {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => (
            <PersonLogoCard
                personInfo={{
                    firstName: row.original.personOrgShortProfile.firstName,
                    lastName: row.original.personOrgShortProfile.lastName,
                    orgName: row.original.personOrgShortProfile.orgName,
                    personOrgGUID: row.original.personOrgShortProfile.personOrgGUID,
                }}
            />
        ),
        sortingFn: 'alphanumeric',
    }),
    columnHelper.accessor(
        (data) =>
            [
                data.personOrgShortProfile.primaryEmail?.plainText,
                data.personOrgShortProfile.otherEmail?.plainText,
                data.personOrgShortProfile.personalPhone?.plainText,
                data.personOrgShortProfile.businessPhone?.plainText,
            ]
                .filter(Boolean)
                .join(' '),
        {
            id: 'contact',
            header: 'Contact',
            cell: ({ row }) => (
                <VerticalLayout $gap={4} onClick={(e) => e.stopPropagation()}>
                    <Tooltip tooltip="Go to email" placement="top-start">
                        <TextLink to={`mailto:${row.original.personOrgShortProfile.primaryEmail?.plainText}`}>
                            {row.original.personOrgShortProfile.primaryEmail?.plainText}
                        </TextLink>
                    </Tooltip>
                    <CopyToClipboard
                        tooltip="Copy Phone #"
                        textToCopy={row.original.personOrgShortProfile.personalPhone?.plainText}
                        tooltipPlacement="top-start"
                    >
                        <Text>{formatPhoneNumber(row.original.personOrgShortProfile.personalPhone?.plainText)}</Text>
                    </CopyToClipboard>
                </VerticalLayout>
            ),
            enableSorting: false,
        },
    ),
    columnHelper.accessor(
        (data) =>
            [
                data.personOrgShortProfile.street?.plainText,
                data.personOrgShortProfile.city?.plainText,
                data.personOrgShortProfile.state,
                data.personOrgShortProfile.postalCode?.plainText,
            ]
                .filter(Boolean)
                .join(' ')
                .trim() || undefined,
        {
            id: 'primaryAddress',
            header: 'Primary Address',
            cell: ({ row }) => {
                const addressLine1 = row.original.personOrgShortProfile.street?.plainText;
                const addressLine2 = [
                    row.original.personOrgShortProfile.city?.plainText,
                    row.original.personOrgShortProfile.state,
                    row.original.personOrgShortProfile.postalCode?.plainText,
                ]
                    .filter(Boolean)
                    .join(', ');
                return (
                    <VerticalLayout $gap={4}>
                        {addressLine1 && <Text>{addressLine1}</Text>}
                        {addressLine2 && <Text>{addressLine2}</Text>}
                    </VerticalLayout>
                );
            },
            sortingFn: 'alphanumeric',
            sortUndefined: 'last',
        },
    ),
    columnHelper.accessor((data) => data.personOrgShortProfile.dateOfBirth?.plainText, {
        id: 'dateOfBirth',
        header: 'Date of Birth',
        cell: ({ row }) => {
            const dateOfBirth = row.original.personOrgShortProfile.dateOfBirth?.plainText;
            if (!dateOfBirth) {
                return <Text>Not provided</Text>;
            }
            const age = calculateAge(dateOfBirth);
            return (
                <VerticalLayout $gap={4}>
                    <Text>{formatDate(dateOfBirth)}</Text>
                    {age !== undefined && <Text>Age: {age}</Text>}
                </VerticalLayout>
            );
        },
        sortingFn: 'datetime',
        sortUndefined: 'last',
        size: 150,
    }),
    columnHelper.accessor('lastLoginAt', {
        id: 'lastLogin',
        header: 'Latest Login',
        cell: ({ getValue }) => <DateTimeDisplay dateString={getValue()} />,
        sortingFn: 'datetime',
        sortUndefined: -1,
        size: 150,
    }),
    columnHelper.accessor((data) => data.personOrgShortProfile?.primaryEmail?.plainText, {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
            const personalPhone = row.original?.personOrgShortProfile?.personalPhone?.plainText;
            const primaryEmail = row.original?.personOrgShortProfile?.primaryEmail?.plainText;
            return (
                <HorizontalLayout $gap={4} onClick={(e) => e.stopPropagation()}>
                    <CopyToClipboard textToCopy={personalPhone} disabled={!personalPhone} tooltip="Copy Phone #">
                        <Button size="icon" variant="outline" disabled={!personalPhone}>
                            <VerticalLayout $rowCenter>
                                <StyledIconPhone />
                            </VerticalLayout>
                        </Button>
                    </CopyToClipboard>
                    <CopyToClipboard textToCopy={primaryEmail} disabled={!primaryEmail} tooltip="Copy Email">
                        <Button size="icon" variant="outline" disabled={!primaryEmail}>
                            <VerticalLayout $rowCenter>
                                <StyledIconEnvelop />
                            </VerticalLayout>
                        </Button>
                    </CopyToClipboard>
                </HorizontalLayout>
            );
        },
        enableSorting: false,
        size: 150,
    }),
];

const iconStyle = css`
    height: 16px;
    width: 16px;
    margin: 6px;
    padding: 0;
`;

const StyledIconPhone = styled(IconPhone)`
    ${iconStyle}
`;

const StyledIconEnvelop = styled(IconEnvelop)`
    ${iconStyle}
`;
